import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  TeamOutlined,
  UserOutlined,
  FileSearchOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  BellOutlined,
  FileTextOutlined,
  MonitorOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';

/**
 * 侧边栏菜单组件
 * 采用与开发者平台一致的白色背景和浅蓝色选中样式
 */
export const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();

  // 判断是否为超级管理员
  const isSuperAdmin = user?.role === 'super_admin';

  // 菜单项定义
  const items: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <HomeOutlined />,
      label: <Link to="/dashboard">概况</Link>,
    },
    {
      key: '/tenants',
      icon: <TeamOutlined />,
      label: <Link to="/tenants">开发者管理</Link>,
    },
    {
      key: '/categories',
      icon: <AppstoreOutlined />,
      label: <Link to="/categories">产品类别</Link>,
    },
    // 只有 super_admin 才能看到操作员管理菜单
    ...(isSuperAdmin ? [{
      key: '/operators',
      icon: <UserOutlined />,
      label: <Link to="/operators">操作员管理</Link>,
    }] : []),
    {
      key: '/audit',
      icon: <FileSearchOutlined />,
      label: <Link to="/audit">审计日志</Link>,
    },
    // 系统监控菜单
    {
      key: 'monitor',
      icon: <BellOutlined />,
      label: '系统监控',
      children: [
        {
          key: '/monitor/health',
          icon: <DashboardOutlined />,
          label: <Link to="/monitor/health">服务器健康</Link>,
        },
        {
          key: '/monitor/alert-configs',
          icon: <BellOutlined />,
          label: <Link to="/monitor/alert-configs">告警配置</Link>,
        },
        {
          key: '/monitor/alert-logs',
          icon: <FileTextOutlined />,
          label: <Link to="/monitor/alert-logs">告警记录</Link>,
        },
        {
          key: '/monitor/meta-monitor',
          icon: <MonitorOutlined />,
          label: <Link to="/monitor/meta-monitor">元监控</Link>,
        },
      ],
    },
  ];

  // 根据当前路径设置选中项
  const getSelectedKey = (): string[] => {
    const path = location.pathname;
    if (path.startsWith('/tenants')) return ['/tenants'];
    if (path.startsWith('/categories')) return ['/categories'];
    if (path.startsWith('/operators')) return ['/operators'];
    if (path.startsWith('/audit')) return ['/audit'];
    if (path.startsWith('/monitor/health')) return ['/monitor/health'];
    if (path.startsWith('/monitor/alert-configs')) return ['/monitor/alert-configs'];
    if (path.startsWith('/monitor/alert-logs')) return ['/monitor/alert-logs'];
    if (path.startsWith('/monitor/meta-monitor')) return ['/monitor/meta-monitor'];
    return ['/dashboard'];
  };

  // 管理展开的菜单项
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const path = location.pathname;
    if (path.startsWith('/monitor/')) return ['monitor'];
    return [];
  });

  // 当路由变化到监控页面时，自动展开系统监控菜单
  useEffect(() => {
    if (location.pathname.startsWith('/monitor/')) {
      const raf = requestAnimationFrame(() => {
        setOpenKeys((prev) => (prev.includes('monitor') ? prev : [...prev, 'monitor']));
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [location.pathname]);

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* 菜单 - 采用与开发者平台一致的样式 */}
      <Menu
        mode="inline"
        selectedKeys={getSelectedKey()}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={items}
        className="flex-1 border-0 bg-white"
        style={{
          '--ant-menu-item-active-bg': '#f0f7ff',
          '--ant-menu-item-selected-bg': '#e6f4ff',
          '--ant-menu-item-selected-color': '#165DFF',
          '--ant-menu-hover-bg': '#f5faff',
          '--ant-menu-border-color': 'transparent',
          padding: '8px 0'
        } as React.CSSProperties}
      />
      
      {/* 侧边栏底部 - 版本信息 */}
      {/* <div className="p-4 border-t border-gray-200 text-xs text-gray-400 text-center">
        v1.0.0
      </div> */}
    </div>
  );
};